import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {useLocation, useHistory} from 'react-router-dom';
import {addAoASheet, createWorkbook, downloadWorkbook} from 'utils/excelExport';
import styled, {useTheme} from 'styled-components';
import Layout from './Layout';
import LayoutHeader, {LayoutHeaderIconTypes} from 'components/LayoutHeader';
import NavCrumb from '../components/Navigation/NavCrumb';
import StyledContainer from '../components/Container';
import Grid from '@material-ui/core/Grid';
import {useSelector, useDispatch} from 'react-redux';
import {fetchRetailDevices, fetchStoreHealthChartData} from '../redux/actions';
import {useOktaAuth} from '@okta/okta-react';
import ApexLineChart from '../components/Charts/ApexLineChart';
import Paper from '../components/Paper';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CircleLoading from '../components/Loading/CircleLoading';
import Table from '../components/Tables/TableStructure/Table';
import TableText from '../components/Tables/TableStructure/TableText';
import SGTitle from '../components/Typography/SGTitle';
import Button from '@material-ui/core/Button';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Icon from '@mdi/react';
import {mdiDownloadOutline} from '@mdi/js';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import formatLabel from '../utils/formatLabel';
import Switch from '@material-ui/core/Switch';
import {useSiteState} from '../hooks/useSiteInfo/SiteStateProvider';
import useRetailHardwareStatusLogs from 'hooks/useRetailHardwareStatusLogs';
import {getThemedSelectMenuProps} from 'utils/muiMenuProps';
import {
  clearPendingStoreHcDeviceSearch,
  peekPendingStoreHcDeviceSearch,
} from 'utils/storeHcPendingSearch';

/** Persists across navigations in the tab; cleared when the tab session ends. */
const RHS_STORE_VIEW_LAND_SESSION_KEY = 'insights:rhsStoreViewLandLoggedIds';

function readRhsStoreViewLoggedIds(): Set<string> {
  if (typeof sessionStorage === 'undefined') {
    return new Set();
  }
  try {
    const raw = sessionStorage.getItem(RHS_STORE_VIEW_LAND_SESSION_KEY);
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

/** Returns true the first time this storeId is seen in the current browser tab session. */
function markRhsStoreViewLandIfFirstInSession(storeId: string): boolean {
  const ids = readRhsStoreViewLoggedIds();
  if (ids.has(storeId)) {
    return false;
  }
  ids.add(storeId);
  if (typeof sessionStorage === 'undefined') {
    // In non-browser contexts, avoid touching sessionStorage and treat as not first-in-session.
    return false;
  }
  try {
    sessionStorage.setItem(RHS_STORE_VIEW_LAND_SESSION_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // ignore quota / private mode
  }
  return true;
}

const StyledStoreHC = styled.div`
  .metric-card {
    height: 100%;
    min-height: 48px;
    font-family: 'Trade Gothic Bold Condensed', 'Trade Gothic', 'Roboto', sans-serif;
  }

  /* Make the numeric value the visual focal point */
  .metric-value {
    font-size: clamp(1.6rem, 2.5vw + 0.5rem, 1.9rem);
    font-weight: 800;
    color: ${props => props.theme.activeStateBlue};
    margin: 4px 0 2px;
    text-align: center;
    line-height: 1.1;
    font-family: 'Trade Gothic Bold Condensed', 'Trade Gothic', 'Roboto', sans-serif;

    .metric-subline {
      display: block;
      font-size: clamp(0.8rem, 1.8vw, 1rem);
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      line-height: 1.25rem;
      padding: 4px 0 0;
      color: ${props => props.theme.activeStateBlue};
      word-break: break-word;
      white-space: normal;

      .metric-subline-label {
        color: ${props => props.theme.text};
        opacity: 0.85;
        margin-right: 6px;
      }

      .metric-subline-value {
        font-weight: 600;
        font-size: clamp(1.6rem, 2vw, 1.3rem);
        &.green {
          color: ${props => props.theme.green};
        }

        &.red {
          color: ${props => props.theme.red};
        }
      }
    }
  }

  .metric-label {
    color: ${props => props.theme.text};
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .metric-card-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0rem 0rem;
    height: 100%;
  }

  .totals-row {
    font-weight: bold;
  }

  /* Make table rows on this page more compact vertically */
  .storehc-table-dense tbody td {
    padding-top: 4px;
    padding-bottom: 4px;
  }

  /* Make breadcrumb text slightly larger on this page */
  .storehc-breadcrumb a {
    font-size: 0.8rem;
  }

  /* Responsive spacing for breadcrumb wrapper */
  .storehc-breadcrumb-wrapper {
    margin-top: 0;
    margin-bottom: 0;
  }

  @media (max-width: 960px) {
    .storehc-breadcrumb-wrapper {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
`;

const StyledMetricCard = styled(Card)`
  background-color: ${props => props.theme.cardBg};
  border-radius: 8px;
  height: 100%;
  min-width: 69px;
  min-height: 69px;
  margin: 4px 0;
  box-shadow: none;
  transition: 200ms;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
  }
`;

const StyledMetricTitle = styled(SGTitle)`
  width: 100%;
  word-break: normal;
  overflow-wrap: anywhere;
  hyphens: auto;
  text-align: center;
  line-height: 1.2;

  /* Slightly de-emphasize the title so the number stands out more */
  && {
    font-size: clamp(0.7rem, 1.4vw, 1rem);
    font-weight: 500;
    font-family: 'Trade Gothic Bold Condensed', 'Trade Gothic', 'Roboto', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${props => props.theme.text};
    opacity: 0.85;
  }
`;

const StyledSectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
`;

const StyledSectionHeaderTitle = styled(SGTitle)`
  flex: 1;
`;

const StyledExportButton = styled(Button)`
  text-transform: none;
  font-family: 'Trade Gothic Bold Condensed';
  align-self: flex-start;
  background-color: ${props => props.theme.activeStateBlue};
  color: ${props => props.theme.cardBg};
  border-radius: 10px;
  border: 1px solid ${props => props.theme.defaultNavy};
  &:hover {
    background-color: ${props => props.theme.activeStateBlue};
    filter: brightness(0.9);
    &.MuiButton-root.Mui-disabled {
      background-color: ${props => props.theme.grey}80;
      color: ${props => props.theme.defaultNavy};
    }
  }
`;

const StyledDeviceTypeButton = styled(Button)`
  color: ${props => props.theme.text};
  text-transform: none;
  padding: 0.5rem 0rem;
  min-width: 0;
  text-decoration: underline;
  font-weight: 500;
  background: transparent;
  &:hover {
    color: ${props => props.theme.activeStateBlue};
    background: transparent;
    text-decoration: underline;
  }
`;

const StyledModalBox = styled(Paper)`
  width: min(80vw, 95vw);
  margin: 10vh auto;
  padding: 24px;
  outline: none;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const StyledCloseIcon = styled(CloseIcon)`
  color: ${props => props.theme.text};
`;

const StyledModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledDeviceList = styled.div`
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* Region search (option A): subtle accent wash over cardBg + left rail; default text colors. */
  .storehc-table-dense tbody tr.storehc-device-row-highlight td {
    /* ~16% / ~24% alpha on activeStateBlue: fallback when color-mix() is unsupported (see browserslist). */
    background-color: ${props => `${props.theme.activeStateBlue}29`} !important;
    background-color: color-mix(
      in srgb,
      ${props => props.theme.activeStateBlue} 16%,
      ${props => props.theme.cardBg}
    ) !important;
    box-shadow: inset 3px 0 0 ${props => props.theme.activeStateBlue};
  }

  .storehc-table-dense tbody tr.storehc-device-row-highlight:hover td {
    background-color: ${props => `${props.theme.activeStateBlue}3D`} !important;
    background-color: color-mix(
      in srgb,
      ${props => props.theme.activeStateBlue} 24%,
      ${props => props.theme.cardBg}
    ) !important;
    box-shadow: inset 3px 0 0 ${props => props.theme.activeStateBlue};
  }
`;

const StyledModalMetricsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
`;

const StyledModalMetricCard = styled.div`
  flex: 1;
  min-width: 180px;
  border: 2px solid ${props => props.theme.navText};
  border-radius: 8px;
  padding: 12px 16px;
  text-align: center;
  background: ${props => props.theme.cardBg};
`;

const StyledModalMetricLabel = styled(Typography)`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${props => props.theme.text};
`;

const StyledModalMetricValue = styled(Typography)<{colorVariant?: 'red' | 'green'}>`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props =>
    props.colorVariant === 'red'
      ? props.theme.red
      : props.colorVariant === 'green'
      ? props.theme.green
      : props.theme.activeStateBlue};
`;

const StyledModalFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StyledModalFilterControl = styled(FormControl)`
  min-width: 150px;
  display: grid;
  grid-template-columns: fit-content(40%) minmax(0, 1fr);
  grid-column-gap: 8px;
  grid-row-gap: 4px;
  align-items: center;
  padding: 0px 12px;
  opacity: ${props => (props.disabled ? 0.6 : 1)};
`;

const StyledModalFilterLabel = styled.span`
  font-family: 'Trade Gothic Bold Condensed';
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.theme.text};
`;

const StyledModalFilterSelect = styled(Select)`
  width: 100%;

  .MuiOutlinedInput-root {
    border-radius: 10px;
  }

  .MuiOutlinedInput-notchedOutline {
    border-radius: 10px;
    border: 1px solid ${props => props.theme.grey};
    transition: border 120ms ease-in-out;
  }

  &:hover .MuiOutlinedInput-notchedOutline,
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: 2px solid ${props => props.theme.text};
  }

  .MuiOutlinedInput-input {
    font-family: 'Roboto', sans-serif;
    padding: 7px 34px 7px 12px;
    font-size: 12px;
    color: ${props => props.theme.grey};
    transition: color 120ms ease-in-out;
  }

  &:hover .MuiOutlinedInput-input,
  &.Mui-focused .MuiOutlinedInput-input {
    color: ${props => props.theme.text};
  }

  svg {
    color: ${props => props.theme.grey};
    width: 16px;
    transition: color 120ms ease-in-out;
  }

  &:hover svg,
  &.Mui-focused svg {
    color: ${props => props.theme.text};
  }

  legend {
    display: none;
  }
`;

const StyledModalFilterClear = styled.button`
  border: none;
  background: none;
  font-family: 'Trade Gothic Bold Condensed';
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.theme.activeStateBlue};
  cursor: pointer;
  align-self: center;
  padding: 0 8px;

  &:disabled {
    color: ${props => props.theme.disabledStateText};
    cursor: not-allowed;
  }
`;

const StyledModalFilterMenuItem = styled(MenuItem)`
  min-width: 150px;
  font-family: 'Roboto', sans-serif;
  padding-left: 32px;
  color: ${props => props.theme.text};
  background-color: transparent;
  transition: color 120ms ease-in-out, background-color 120ms ease-in-out;

  &:hover {
    color: ${props => props.theme.defaultNavy};
    background-color: ${props => props.theme.blue2};
    box-shadow: inset 4px 0 0 ${props => props.theme.grey};
  }

  &.Mui-selected.MuiMenuItem-root {
    color: ${props => props.theme.selectMenuSelectedText || props.theme.defaultNavy} !important;
    background-color: ${props => props.theme.blue2} !important;
    font-weight: 600;
    box-shadow: inset 4px 0 0 ${props => props.theme.disabledStateBlue} !important;
  }

  &.Mui-selected.MuiMenuItem-root:hover {
    color: ${props => props.theme.selectMenuSelectedText || props.theme.defaultNavy} !important;
    background-color: ${props => props.theme.blue3} !important;
  }
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SwitchLabel = styled.span`
  font-family: 'Trade Gothic';
  color: ${props => props.theme.text};
`;

const InventoryDescriptionCellContent = styled.div`
  max-height: 4.5rem;
  overflow-y: auto;
  padding-right: 4px;
  word-break: break-word;
  white-space: pre-wrap;
  color: ${props => props.theme.text};
`;

const LastSeenCellContent = styled.div`
  white-space: pre-line;
  color: ${props => props.theme.text};
`;

const renderModalFilterValue = (value: unknown) => {
  const selectedValue = value as string;
  if (!selectedValue) {
    return 'All';
  }
  return formatLabel(selectedValue);
};

const formatInventoryStatusDisplay = (status?: string | null) => {
  const normalized = (status ?? '').toString().trim();
  if (!normalized) {
    return 'Unknown';
  }
  return normalized.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const formatLastSeenDisplay = (value: string | number | Date | null | undefined) => {
  if (!value) {
    return 'Unknown';
  }
  const dateObj = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateObj.getTime())) {
    return 'Unknown';
  }
  const datePart = dateObj.toLocaleDateString();
  const timePart = dateObj.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
  return `${datePart}\n ${timePart}`;
};

type DeviceFilterKey =
  | 'deviceType'
  | 'deviceName'
  | 'serialNumber'
  | 'model'
  | 'inventoryCheckStatus'
  | 'networkCheckStatus'
  | 'itamInventoryStatusDescription';

type DeviceFilterMap = Record<DeviceFilterKey, string>;

type TrendingSeriesPoint = {
  x: number;
  y: number;
};

type TrendingSeriesKey = 'Network Status' | 'Inventory Issues';
const NETWORK_STATUS = 'Network Status';
const INVENTORY_ISSUES = 'Inventory Issues';

const createDeviceFilters = (): DeviceFilterMap => ({
  deviceType: '',
  deviceName: '',
  serialNumber: '',
  model: '',
  inventoryCheckStatus: '',
  networkCheckStatus: '',
  itamInventoryStatusDescription: '',
});

const deviceFilterConfig: Array<{id: DeviceFilterKey; label: string}> = [
  {id: 'deviceType', label: 'Device Type'},
  {id: 'deviceName', label: 'Device Name'},
  {id: 'serialNumber', label: 'Serial Number'},
  {id: 'model', label: 'Model'},
  {id: 'networkCheckStatus', label: 'Network Status'},
  {id: 'inventoryCheckStatus', label: 'ServiceNow Inventory Status'},
];

function ScrollToTopOnMount() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return null;
}

interface MetricCardProps {
  label: string;
  sublineLabel?: string;
  value: React.ReactNode;
  loading?: boolean;
  valueColorVariant?: 'red' | 'green';
  sublineColorVariant?: 'red' | 'green';
  onClick?: () => void;
}

function MetricCard({
  label,
  sublineLabel,
  value,
  loading,
  valueColorVariant,
  sublineColorVariant,
  onClick,
}: MetricCardProps) {
  const theme = useTheme() as any;
  const redColor = theme?.red || '#F44336';
  const greenColor = theme?.green || '#4CAF50';
  const activeStateBlueColor = theme?.activeStateBlue || '#9EDAE5';
  const resolvedValueColor =
    valueColorVariant === 'red'
      ? redColor
      : valueColorVariant === 'green'
      ? greenColor
      : activeStateBlueColor;

  const resolvedSublineColor =
    sublineColorVariant === 'red'
      ? redColor
      : sublineColorVariant === 'green'
      ? greenColor
      : activeStateBlueColor;

  return (
    <StyledMetricCard elevation={0} className="metric-card">
      <CardContent className="metric-card-content">
        <Paper
          style={{
            minHeight: '48px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            cursor: onClick ? 'pointer' : 'default',
          }}
          onClick={onClick}
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : undefined}
          onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (onClick && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              onClick();
            }
          }}
        >
          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100px',
              }}
            >
              <CircleLoading />
            </div>
          ) : (
            <>
              <StyledMetricTitle className={'text-color'} variant={'t2'}>
                <span>{label} </span>
                {sublineLabel && (
                  <span className="metric-value" style={{color: resolvedSublineColor}}>
                    {sublineLabel}
                  </span>
                )}
              </StyledMetricTitle>
              <Typography className="metric-value" style={{color: resolvedValueColor}}>
                {value}
              </Typography>
            </>
          )}
        </Paper>
      </CardContent>
    </StyledMetricCard>
  );
}

function StoreHC(props: any) {
  const {match} = props;
  const location = useLocation();
  const theme = useTheme() as any;
  const modalSelectMenuProps = useMemo(() => getThemedSelectMenuProps(theme, {inModal: true}), [
    theme,
  ]);

  const dispatch = useDispatch();
  const storeId = match.params.storeId;

  const {logStoreViewLand, logDeviceTypeModalOpen, logExport} = useRetailHardwareStatusLogs();

  // Once page is loaded and authenticated, fetch data for store.
  const {authState} = useOktaAuth();
  const rawAccessToken = authState?.accessToken;
  const accessToken =
    typeof rawAccessToken === 'string' ? rawAccessToken : rawAccessToken?.accessToken;
  const [trendingSeriesKey, setTrendingSeriesKey] = useState<TrendingSeriesKey>(NETWORK_STATUS);

  useEffect(() => {
    if (authState?.isAuthenticated && storeId && accessToken) {
      dispatch(fetchRetailDevices(accessToken, storeId));
      dispatch(fetchStoreHealthChartData(accessToken, storeId));
    }
  }, [authState, storeId, accessToken, dispatch]);

  const storeHealthChartRawData = useSelector(
    (state: any) => state.data.app.storeHealthChartData.data
  );
  const storeHealthChartLoaded = useSelector(
    (state: any) => state.data.app.storeHealthChartData.dataRetrieved
  );
  const retailDevices = useSelector((state: any) => state.retailDevices.data);
  const retailDevicesLoading = useSelector((state: any) => state.retailDevices.loading);
  const retailDevicesError = useSelector((state: any) => state.retailDevices.error);
  const {state: siteState} = useSiteState();
  const siteStateSites = siteState?.sites || [];
  const storeReportRows = useMemo(() => (Array.isArray(retailDevices) ? retailDevices : []), [
    retailDevices,
  ]);
  const storeReportLoaded = !retailDevicesLoading && !retailDevicesError;

  const chartColors = useMemo(() => {
    return trendingSeriesKey === NETWORK_STATUS
      ? [theme?.green || '#4CAF50']
      : [theme?.red || '#F44336'];
  }, [trendingSeriesKey, theme]);

  const [selectedChartTime, setSelectedChartTime] = useState('one_week');

  const yAxisTitle = useMemo(() => {
    return trendingSeriesKey === NETWORK_STATUS ? '%' : 'Number of Devices';
  }, [trendingSeriesKey]);

  const buildHealthSeries = useCallback(
    (valueSelector: (entry: any) => number | null | undefined) => {
      if (!Array.isArray(storeHealthChartRawData)) return [];
      const points = storeHealthChartRawData
        .map((entry: any) => {
          const rawDate = entry?.date || entry?.Date || entry?.timestamp;
          const timestamp = rawDate ? new Date(rawDate).getTime() : NaN;
          if (Number.isNaN(timestamp)) {
            return null;
          }
          const value = Number(valueSelector(entry));
          if (!Number.isFinite(value)) {
            return null;
          }
          return {
            x: timestamp,
            y: value,
          };
        })
        .filter((point: TrendingSeriesPoint | null): point is TrendingSeriesPoint => !!point);
      if (points.length) {
        points.sort((a: TrendingSeriesPoint, b: TrendingSeriesPoint) => a.x - b.x);
        return points;
      }
      return [];
    },
    [storeHealthChartRawData]
  );

  const activeHealthSeriesPoints = useMemo(
    () =>
      buildHealthSeries((entry: any) => {
        const totalDevices = Number(
          entry?.totalDevices ?? entry?.TotalDevices ?? entry?.totalDevicesCount ?? 0
        );
        const recentlySeenDevices = Number(
          entry?.recentlySeenDevices ??
            entry?.RecentlySeenDevices ??
            entry?.recentlySeen ??
            entry?.recentlySeenCount ??
            0
        );
        if (totalDevices <= 0) return 0;
        return Number(((recentlySeenDevices / totalDevices) * 100).toFixed(2));
      }),
    [buildHealthSeries]
  );

  const inventoryIssuesSeriesPoints = useMemo(
    () =>
      buildHealthSeries((entry: any) => {
        const totalDevices = Number(
          entry?.totalDevices ?? entry?.TotalDevices ?? entry?.totalDevicesCount ?? 0
        );
        const properStatusDevices = Number(
          entry?.devicesWithProperServiceNowStatus ??
            entry?.DevicesWithProperServiceNowStatus ??
            entry?.properServiceNowStatusCount ??
            0
        );
        if (totalDevices <= 0) return 0;
        return Number(totalDevices - properStatusDevices);
      }),
    [buildHealthSeries]
  );

  const trendingSeriesByKey = useMemo(() => {
    const activeSeries = [
      {
        name: '% of Active Devices',
        data: activeHealthSeriesPoints,
      },
    ];
    return {
      [NETWORK_STATUS]: activeSeries,
      [INVENTORY_ISSUES]: [
        {
          name: INVENTORY_ISSUES,
          data: inventoryIssuesSeriesPoints,
        },
      ],
    };
  }, [activeHealthSeriesPoints, inventoryIssuesSeriesPoints]);

  const networkStatusYAxisMin = useMemo(() => {
    if (!activeHealthSeriesPoints.length) {
      return 0;
    }

    const lowestValue = Math.min(...activeHealthSeriesPoints.map(point => point.y));

    // Keep min below the lowest value and rounded to a multiple of 5
    const paddedMin = Math.floor((lowestValue - 2) / 5) * 5;

    // Never go below 0, and never start above 90
    return Math.min(Math.max(0, paddedMin), 90);
  }, [activeHealthSeriesPoints]);

  const storeTrendingSeries = trendingSeriesByKey[trendingSeriesKey];

  const hasTrendingSeries =
    Array.isArray(storeTrendingSeries) &&
    storeTrendingSeries.some(series => Array.isArray(series?.data) && series.data.length > 0);
  const storeTrendingChartLoaded = storeHealthChartLoaded || hasTrendingSeries;

  const handleTrendingSwitchToggle = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setTrendingSeriesKey(checked ? NETWORK_STATUS : INVENTORY_ISSUES);
    },
    []
  );

  const deviceTotals = useMemo(() => {
    return storeReportRows.reduce(
      (
        acc: {
          totalDevices: number;
          impactedDevices: number;
          notRecentlySeenDevices: number;
          inventoryIssuesDevices: number;
        },
        row: any
      ) => {
        const isActive = !!row?.isActive;
        const isImpacted = isActive && (!row?.isRecentlySeen || !row?.isDeviceHealthy);
        return {
          totalDevices: acc.totalDevices + (isActive ? 1 : 0),
          notRecentlySeenDevices:
            acc.notRecentlySeenDevices + (isActive && !row?.isRecentlySeen ? 1 : 0),
          inventoryIssuesDevices:
            acc.inventoryIssuesDevices + (isActive && !row?.isDeviceHealthy ? 1 : 0),
          impactedDevices: acc.impactedDevices + (isImpacted ? 1 : 0),
        };
      },
      {totalDevices: 0, impactedDevices: 0, notRecentlySeenDevices: 0, inventoryIssuesDevices: 0}
    );
  }, [storeReportRows]);
  const totalNumberOfDevices = deviceTotals.totalDevices;
  const totalNumberOfImpactedDevices = deviceTotals.impactedDevices;
  const totalNumberOfNotRecentlySeenDevices = deviceTotals.notRecentlySeenDevices;
  const totalNumberOfInventoryIssuesDevices = deviceTotals.inventoryIssuesDevices;
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>('');
  const [selectedDeviceDevices, setSelectedDeviceDevices] = useState<any[]>([]);
  const [isAllDeviceTypesModal, setIsAllDeviceTypesModal] = useState<boolean>(false);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState<boolean>(false);
  const [deviceTableFilters, setDeviceTableFilters] = useState<DeviceFilterMap>(
    createDeviceFilters()
  );
  /** Row highlight for region-search deep link only (no serial filter — full category list stays visible). */
  const [highlightedDeviceSerialForSearch, setHighlightedDeviceSerialForSearch] = useState<
    string | null
  >(null);
  /** Avoid clearing filters on initial mount when modal is false (would wipe search deep-link). */
  const prevIsDeviceModalOpenRef = useRef<boolean | null>(null);
  useEffect(() => {
    prevIsDeviceModalOpenRef.current = null;
  }, []);

  const closeDeviceModal = () => {
    setIsDeviceModalOpen(false);
    setIsAllDeviceTypesModal(false);
  };

  const aggregatedStoreReportData = useMemo(() => {
    const groups: Record<
      string,
      {
        deviceType: string;
        totalDevices: number;
        notRecentlySeen: number;
        inventoryIssues: number;
        totalIssues: number;
      }
    > = {};

    storeReportRows.forEach(row => {
      const deviceType = String(row?.DeviceType || row?.deviceType || 'Unknown Device');
      row.deviceType = deviceType;
      if (!groups[deviceType]) {
        groups[deviceType] = {
          deviceType,
          totalDevices: 0,
          notRecentlySeen: 0,
          inventoryIssues: 0,
          totalIssues: 0,
        };
      }
      row.inventoryCheckStatus = formatInventoryStatusDisplay(
        row.inventoryCheckStatus || row.itamInventoryStatus || 'Unknown'
      );
      row.networkCheckStatus = row.isRecentlySeen ? 'Active in Store' : 'Inactive in Store';
      row.lastSeenDate = row.lastSeen ? row.lastSeen : null;
      row.impacted = row.isActive && (!row.isRecentlySeen || !row.isDeviceHealthy);
      const group = groups[deviceType];
      group.totalDevices += 1;
      if (!row?.isRecentlySeen) {
        group.notRecentlySeen += 1;
        group.totalIssues += 1;
      }
      if (!row?.isDeviceHealthy) {
        group.inventoryIssues += 1;
        group.totalIssues += 1;
      }
    });
    return Object.values(groups);
  }, [storeReportRows]);

  const deviceDetailsByType = useMemo(() => {
    return storeReportRows.reduce((acc: Record<string, any[]>, device) => {
      const deviceType = String(device?.DeviceType || device?.deviceType || 'Unknown Device');
      if (!acc[deviceType]) {
        acc[deviceType] = [];
      }
      acc[deviceType].push({
        deviceType,
        deviceName: device.deviceName ? device.deviceName : 'Unknown',
        serialNumber: device.serialNumber ? device.serialNumber : 'Unknown',
        model: device.model ? device.model : 'Unknown',
        inventoryCheckStatus: formatInventoryStatusDisplay(
          device.itamInventoryStatus ? device.itamInventoryStatus : 'Unknown'
        ),
        itamInventoryStatusDescription: device.itamInventoryStatusDescription
          ? device.itamInventoryStatusDescription
          : '',
        networkCheckStatus: device.isRecentlySeen ? 'Active in Store' : 'Inactive in Store',
        lastSeenDate: device.lastSeen || device.LastSeen || device.lastSeenDate || null,
        impacted: device.isActive && (!device.isRecentlySeen || !device.isDeviceHealthy),
      });
      return acc;
    }, {});
  }, [storeReportRows]);

  // Auto-open the device-detail modal when arriving from a device search hit.
  // Dedupe by location.key + store + device so a new search pick re-applies even on the same store.
  const history = useHistory();
  const lastAppliedDeepLinkFingerprintRef = useRef<string | null>(null);
  useEffect(() => {
    lastAppliedDeepLinkFingerprintRef.current = null;
    prevIsDeviceModalOpenRef.current = null;
  }, [storeId]);

  useEffect(() => {
    const searchedDeviceFromState = (location.state as any)?.searchedDevice;
    const pending = searchedDeviceFromState ? null : peekPendingStoreHcDeviceSearch(storeId);
    const searchedDevice = searchedDeviceFromState || pending?.searchedDevice;
    if (!searchedDevice) {
      return;
    }
    if (!storeReportLoaded || !storeReportRows.length) {
      return;
    }
    const {deviceType, serialNumber, deviceName} = searchedDevice as {
      deviceType: string;
      serialNumber?: string;
      deviceName?: string;
    };
    if (!deviceType) {
      return;
    }
    const serialNorm =
      serialNumber != null && String(serialNumber).trim() !== ''
        ? String(serialNumber).trim().toLowerCase()
        : '';
    const deviceNameNorm =
      deviceName != null && String(deviceName).trim() !== ''
        ? String(deviceName).trim().toLowerCase()
        : '';
    const deepLinkFingerprint = `${location.key}|${storeId}|${String(
      deviceType
    )}|${serialNorm}|${deviceNameNorm}`;
    if (lastAppliedDeepLinkFingerprintRef.current === deepLinkFingerprint) {
      return;
    }

    const resolvedTypeKey =
      deviceDetailsByType[deviceType] != null
        ? deviceType
        : Object.keys(deviceDetailsByType).find(
            k => k.toLowerCase() === String(deviceType).toLowerCase()
          ) || deviceType;
    const devicesForType = deviceDetailsByType[resolvedTypeKey] || [];

    const rowWithSite = storeReportRows.find((r: any) => r?.siteId != null || r?.SiteId != null);
    if (rowWithSite) {
      const rowSite = rowWithSite.siteId ?? rowWithSite.SiteId;
      if (rowSite != null && String(rowSite) !== String(storeId)) {
        return;
      }
    }

    lastAppliedDeepLinkFingerprintRef.current = deepLinkFingerprint;
    setIsAllDeviceTypesModal(false);
    setSelectedDeviceType(resolvedTypeKey);
    setSelectedDeviceDevices(devicesForType);
    setDeviceTableFilters(createDeviceFilters());
    setHighlightedDeviceSerialForSearch(serialNorm !== '' ? serialNorm : null);
    setIsDeviceModalOpen(true);

    const selectedRegionFromPending = pending?.selectedRegion ?? null;

    clearPendingStoreHcDeviceSearch();

    if (searchedDeviceFromState) {
      const {searchedDevice: _omit, ...restState} = (location.state as any) || {};
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: Object.keys(restState).length ? restState : undefined,
      });
    } else if (selectedRegionFromPending != null) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: {
          ...((location.state as any) || {}),
          selectedRegion: selectedRegionFromPending,
        },
      });
    }
  }, [location, storeId, storeReportLoaded, storeReportRows, deviceDetailsByType, history]);

  const filteredDeviceRows = useMemo(() => {
    return selectedDeviceDevices.filter(device => {
      if (
        deviceTableFilters.deviceType &&
        String(device.deviceType || device.DeviceType || 'Unknown Device') !==
          deviceTableFilters.deviceType
      ) {
        return false;
      }
      if (deviceTableFilters.model && device.model !== deviceTableFilters.model) {
        return false;
      }
      if (deviceTableFilters.serialNumber) {
        const filterSerial = String(deviceTableFilters.serialNumber).trim().toLowerCase();
        const rowSerial = String(device.serialNumber ?? '')
          .trim()
          .toLowerCase();
        if (rowSerial !== filterSerial) {
          return false;
        }
      }
      if (deviceTableFilters.deviceName && device.deviceName !== deviceTableFilters.deviceName) {
        return false;
      }
      if (
        deviceTableFilters.inventoryCheckStatus &&
        device.inventoryCheckStatus !== deviceTableFilters.inventoryCheckStatus
      ) {
        return false;
      }
      if (
        deviceTableFilters.networkCheckStatus &&
        device.networkCheckStatus !== deviceTableFilters.networkCheckStatus
      ) {
        return false;
      }
      return true;
    });
  }, [selectedDeviceDevices, deviceTableFilters]);

  const deviceFilterOptions = useMemo<Record<DeviceFilterKey, string[]>>(() => {
    const options: Record<DeviceFilterKey, Set<string>> = {
      deviceType: new Set<string>(),
      deviceName: new Set<string>(),
      serialNumber: new Set<string>(),
      model: new Set<string>(),
      inventoryCheckStatus: new Set<string>(),
      itamInventoryStatusDescription: new Set<string>(),
      networkCheckStatus: new Set<string>(),
    };

    selectedDeviceDevices.forEach(device => {
      const normalizedType = String(device.deviceType || device.DeviceType || 'Unknown Device');
      if (normalizedType) {
        options.deviceType.add(normalizedType);
      }
      if (device.model) {
        options.model.add(device.model);
      }
      if (device.serialNumber) {
        options.serialNumber.add(device.serialNumber);
      }
      if (device.deviceName) {
        options.deviceName.add(device.deviceName);
      }
      if (device.inventoryCheckStatus) {
        options.inventoryCheckStatus.add(device.inventoryCheckStatus);
      }
      if (device.networkCheckStatus) {
        options.networkCheckStatus.add(device.networkCheckStatus);
      }
    });

    return {
      deviceType: Array.from(options.deviceType),
      deviceName: Array.from(options.deviceName),
      serialNumber: Array.from(options.serialNumber),
      model: Array.from(options.model),
      inventoryCheckStatus: Array.from(options.inventoryCheckStatus),
      itamInventoryStatusDescription: Array.from(options.itamInventoryStatusDescription),
      networkCheckStatus: Array.from(options.networkCheckStatus),
    };
  }, [selectedDeviceDevices]);

  const hasActiveDeviceFilters = useMemo(() => {
    return Object.values(deviceTableFilters).some(value => Boolean(value));
  }, [deviceTableFilters]);

  const visibleDeviceFilterConfig = useMemo(() => {
    return isAllDeviceTypesModal
      ? deviceFilterConfig
      : deviceFilterConfig.filter(config => config.id !== 'deviceType');
  }, [isAllDeviceTypesModal]);

  const topImpactedDevices = useMemo(() => {
    return [...aggregatedStoreReportData]
      .filter(group => group.inventoryIssues > 0 || group.notRecentlySeen > 0)
      .sort((a, b) => b.totalIssues - a.totalIssues)
      .slice(0, 3);
  }, [aggregatedStoreReportData]);

  const exportSiteInfo = useMemo(() => {
    if (!storeId || !siteStateSites.length) {
      return null;
    }
    return siteStateSites.find((site: any) => site.siteId === storeId) || null;
  }, [storeId, siteStateSites]);

  const storeDisplayName = useMemo(() => {
    if (exportSiteInfo) {
      const code = exportSiteInfo.siteCode || '';
      const name = exportSiteInfo.siteName || '';
      return `${code} ${name}`.trim() || `Store ${storeId || ''}`;
    }
    return `Store ${storeId || ''}`;
  }, [exportSiteInfo, storeId]);

  const isAuthenticated = Boolean(authState?.isAuthenticated);
  const locationState = location.state;

  useEffect(() => {
    if (!isAuthenticated || !storeId || !accessToken) {
      return;
    }
    if (!markRhsStoreViewLandIfFirstInSession(storeId)) {
      return;
    }
    const navState = locationState as {selectedRegion?: string} | undefined;
    const storeNumberStr =
      exportSiteInfo?.siteCode != null && String(exportSiteInfo.siteCode).length > 0
        ? String(exportSiteInfo.siteCode)
        : undefined;
    logStoreViewLand({
      siteId: storeId,
      storeNumber: storeNumberStr,
      storeName: exportSiteInfo?.siteName,
      selectedRegionFromNav: navState?.selectedRegion,
    });
  }, [isAuthenticated, storeId, accessToken, exportSiteInfo, locationState, logStoreViewLand]);

  const logDeviceTypeModalOpenEvent = useCallback(
    (deviceTypeClicked: string) => {
      const navState = locationState as {selectedRegion?: string} | undefined;
      const storeNumberStr =
        exportSiteInfo?.siteCode != null && String(exportSiteInfo.siteCode).length > 0
          ? String(exportSiteInfo.siteCode)
          : undefined;
      logDeviceTypeModalOpen({
        siteId: storeId,
        storeNumber: storeNumberStr,
        storeName: exportSiteInfo?.siteName,
        selectedRegionFromNav: navState?.selectedRegion,
        deviceTypeClicked,
      });
    },
    [storeId, exportSiteInfo, locationState, logDeviceTypeModalOpen]
  );

  const handleExportToXlsx = useCallback(async () => {
    if (!aggregatedStoreReportData.length && !storeReportRows.length) {
      return;
    }

    logExport({
      exportContext: 'store_detail',
      siteId: storeId || '',
      storeNumber:
        exportSiteInfo?.siteCode != null && String(exportSiteInfo.siteCode).length > 0
          ? String(exportSiteInfo.siteCode)
          : undefined,
      storeName: exportSiteInfo?.siteName,
      sheetSummary: 'multi_sheet_workbook',
    });

    const workbook = createWorkbook();
    const storeNameForExport = exportSiteInfo
      ? `${exportSiteInfo.siteCode} ${exportSiteInfo.siteName}`
      : `Store ${storeId || ''}`;
    const timestamp = new Date().toISOString();
    const metadataRows: Array<Array<string | number>> = [
      ['Store ID', storeId || 'Unknown'],
      ['Store Name', storeNameForExport],
      ['Timestamp', timestamp],
    ];

    const metricsSheetRows: Array<Array<string | number>> = [
      ...metadataRows,
      [''],
      ['Inventory Status Summary'],
      ['Metric', 'Value', 'Details'],
      ['Total # of Devices', totalNumberOfDevices, ''],
      ['# of Devices Failing Tests', totalNumberOfImpactedDevices, ''],
      ['# of Not Recently Seen Devices', totalNumberOfNotRecentlySeenDevices, ''],
      ['# of Devices with Inventory Issues', totalNumberOfInventoryIssuesDevices, ''],
    ];

    if (topImpactedDevices.length) {
      topImpactedDevices.forEach(device => {
        metricsSheetRows.push([
          `Top Device Categories Failing Tests: ${device.deviceType}`,
          `Network: ${device.notRecentlySeen}`,
          `ServiceNow Inventory: ${device.inventoryIssues}`,
        ]);
      });
    }

    if (aggregatedStoreReportData.length) {
      metricsSheetRows.push(['']);
      metricsSheetRows.push(['Device Type Summary']);
      metricsSheetRows.push([
        'Device Type',
        'Total Devices',
        'Not Recently Seen',
        'ServiceNow Inventory Issues',
      ]);
      aggregatedStoreReportData.forEach(row => {
        metricsSheetRows.push([
          row.deviceType,
          row.totalDevices,
          row.notRecentlySeen,
          row.inventoryIssues,
        ]);
      });
    }

    addAoASheet(workbook, 'Metrics', metricsSheetRows);

    if (storeReportRows.length) {
      const deviceSheetHeaders = [
        'Device Type',
        'Device Name',
        'Serial Number',
        'Model',
        'Active',
        'Recently Seen',
        'Last Seen',
        'Device Healthy',
        'ServiceNow Inventory Status',
        'ServiceNow Inventory Status Description',
      ];

      const deviceSheetColumnWidths = [
        {wch: 25}, // Device Type (Device Category)
        {wch: 30}, // Device Name
        {wch: 18}, // Serial Number (Device Serial Number)
        {wch: 25}, // Model
        {wch: 8}, // Active
        {wch: 15}, // Recently Seen
        {wch: 27}, // Last Seen
        {wch: 12}, // Device Healthy
        {wch: 20}, // ServiceNow Inventory Status (Inventory Check Status)
        {wch: 50}, // ServiceNow Inventory Status Description
      ];

      const buildSheetRows = (title: string) => [
        ...metadataRows,
        [''],
        [title],
        deviceSheetHeaders,
      ];

      const allDevicesText = 'All Devices';
      const notRecentlySeenDevicesText = 'Not Recently Seen Devices';
      const inventoryIssuesDevicesText = 'Devices with Inventory Issues';
      const devicesWithoutIssuesText = 'Devices without Issues';

      const deviceSheetRows: Array<Array<string | number>> = buildSheetRows(allDevicesText);
      const notRecentlySeenSheetRows: Array<Array<string | number>> = buildSheetRows(
        notRecentlySeenDevicesText
      );
      const inventoryIssuesSheetRows: Array<Array<string | number>> = buildSheetRows(
        inventoryIssuesDevicesText
      );
      const devicesWithoutIssuesSheetRows: Array<Array<string | number>> = buildSheetRows(
        devicesWithoutIssuesText
      );

      storeReportRows.forEach((device: any) => {
        const lastSeenValue = device.lastSeen || device.LastSeen || device.lastSeenDate;
        const isMobilePaymentTerminal =
          (device.DeviceType || device.deviceType) === 'MobilePaymentTerminal';
        const lastSeenDisplay = isMobilePaymentTerminal
          ? 'Not Available'
          : lastSeenValue
          ? formatLastSeenDisplay(lastSeenValue)
          : '';
        const rawInventoryStatus =
          device.ItamInventoryStatus || device.itamInventoryStatus || 'Unknown';
        const formattedInventoryStatus = formatInventoryStatusDisplay(rawInventoryStatus);
        const rowValues: Array<string> = [
          device.DeviceType || device.deviceType || 'Unknown',
          device.DeviceName || device.deviceName || 'Unknown',
          device.SerialNumber || device.serialNumber || 'Unknown',
          `'${device.Model || device.model || 'Unknown'}`,
          device.IsActive || device.isActive ? 'Yes' : 'No',
          device.IsRecentlySeen || device.isRecentlySeen ? 'Yes' : 'No',
          lastSeenDisplay,
          device.IsDeviceHealthy || device.isDeviceHealthy ? 'Yes' : 'No',
          formattedInventoryStatus,
          device.itamInventoryStatusDescription || '',
        ];
        deviceSheetRows.push(rowValues);
        const [, , , , active, recentlySeen, , deviceHealthy] = rowValues;
        const matchFound = rawInventoryStatus?.toString().trim().toLowerCase() === 'matchfound';
        const isFullyHealthy =
          active === 'Yes' && recentlySeen === 'Yes' && deviceHealthy === 'Yes' && matchFound;
        if (!isFullyHealthy) {
          if (recentlySeen !== 'Yes') {
            notRecentlySeenSheetRows.push(rowValues);
          }
          if (deviceHealthy !== 'Yes') {
            inventoryIssuesSheetRows.push(rowValues);
          }
        } else {
          devicesWithoutIssuesSheetRows.push(rowValues);
        }
      });

      addAoASheet(workbook, allDevicesText, deviceSheetRows, deviceSheetColumnWidths);
      addAoASheet(
        workbook,
        notRecentlySeenDevicesText,
        notRecentlySeenSheetRows,
        deviceSheetColumnWidths
      );
      addAoASheet(
        workbook,
        inventoryIssuesDevicesText,
        inventoryIssuesSheetRows,
        deviceSheetColumnWidths
      );
      addAoASheet(
        workbook,
        devicesWithoutIssuesText,
        devicesWithoutIssuesSheetRows,
        deviceSheetColumnWidths
      );
    }

    const dateStamp = new Date().toISOString().split('T')[0];
    await downloadWorkbook(workbook, `store-${storeId || 'unknown'}-health-${dateStamp}.xlsx`);
  }, [
    aggregatedStoreReportData,
    storeReportRows,
    topImpactedDevices,
    totalNumberOfDevices,
    totalNumberOfImpactedDevices,
    totalNumberOfNotRecentlySeenDevices,
    totalNumberOfInventoryIssuesDevices,
    storeId,
    exportSiteInfo,
    logExport,
  ]);

  useEffect(() => {
    const prev = prevIsDeviceModalOpenRef.current;
    prevIsDeviceModalOpenRef.current = isDeviceModalOpen;
    if (prev === true && isDeviceModalOpen === false) {
      setDeviceTableFilters(createDeviceFilters());
      setHighlightedDeviceSerialForSearch(null);
    }
  }, [isDeviceModalOpen]);

  /** Highlight row only for region-search deep link (not when user filters by serial in the modal). */
  const deviceModalCustomRowClass = useCallback(
    (row: any) => {
      const classes: string[] = [];
      if (row.isTotal) {
        classes.push('totals-row');
      }
      const searchHighlight = highlightedDeviceSerialForSearch;
      if (
        isDeviceModalOpen &&
        searchHighlight &&
        String(row.serialNumber ?? '')
          .trim()
          .toLowerCase() === searchHighlight
      ) {
        classes.push('storehc-device-row-highlight');
      }
      return classes.join(' ').trim();
    },
    [highlightedDeviceSerialForSearch, isDeviceModalOpen]
  );

  const tableData = useMemo(() => {
    if (!aggregatedStoreReportData.length) {
      return [];
    }
    const sourceRows = aggregatedStoreReportData;
    const totals = sourceRows.reduce(
      (acc, row) => ({
        totalDevices: acc.totalDevices + row.totalDevices,
        notRecentlySeen: acc.notRecentlySeen + row.notRecentlySeen,
        inventoryIssues: acc.inventoryIssues + row.inventoryIssues,
      }),
      {totalDevices: 0, notRecentlySeen: 0, inventoryIssues: 0}
    );
    return [
      ...sourceRows,
      {
        deviceType: 'Total',
        totalDevices: totals.totalDevices,
        notRecentlySeen: totals.notRecentlySeen,
        inventoryIssues: totals.inventoryIssues,
        isTotal: true,
      },
    ];
  }, [aggregatedStoreReportData]);

  const deviceDetailsTableColumns = [
    {
      id: 'deviceName',
      label: 'Device Name',
      width: '13%',
    },
    {
      id: 'serialNumber',
      label: 'Serial Number',
      width: '12%',
    },
    {
      id: 'model',
      label: 'Model',
      width: '20%',
    },
    {
      id: 'networkCheckStatus',
      label: 'Network Status',
      width: '15%',
    },
    {
      id: 'lastSeenDate',
      label: 'Last Seen',
      width: '10%',
      format: (value: string | number | Date | null | undefined, row?: {deviceType?: string}) =>
        row?.deviceType === 'MobilePaymentTerminal' ? (
          <LastSeenCellContent>Not Available</LastSeenCellContent>
        ) : (
          <LastSeenCellContent>{formatLastSeenDisplay(value)}</LastSeenCellContent>
        ),
    },
    {
      id: 'inventoryCheckStatus',
      label: 'ServiceNow Inventory Status',
      width: '15%',
    },
    {
      id: 'itamInventoryStatusDescription',
      label: 'ServiceNow Inventory Status Description',
      width: '15%',
      format: (value: string | undefined | null) => (
        <InventoryDescriptionCellContent>{value || ''}</InventoryDescriptionCellContent>
      ),
    },
  ];

  // Define table columns
  const tableColumns = [
    {
      id: 'deviceType',
      label: 'Device Type',
      width: '25%',
      align: 'left',
      icon: 'none',
      format: (value: string, row: any) =>
        row.isTotal ? (
          <StyledDeviceTypeButton
            size="small"
            onClick={() => {
              logDeviceTypeModalOpenEvent('All Device Types');
              setIsAllDeviceTypesModal(true);
              setSelectedDeviceType('All Device Types');
              setSelectedDeviceDevices(storeReportRows);
              setDeviceTableFilters(createDeviceFilters());
              setHighlightedDeviceSerialForSearch(null);
              setIsDeviceModalOpen(true);
            }}
          >
            {value}
          </StyledDeviceTypeButton>
        ) : (
          <StyledDeviceTypeButton
            size="small"
            onClick={() => {
              logDeviceTypeModalOpenEvent(row.deviceType);
              setIsAllDeviceTypesModal(false);
              const devices = deviceDetailsByType[row.deviceType] || [];
              setSelectedDeviceType(row.deviceType);
              setSelectedDeviceDevices(devices);
              setDeviceTableFilters(createDeviceFilters());
              setHighlightedDeviceSerialForSearch(null);
              setIsDeviceModalOpen(true);
            }}
          >
            {value}
          </StyledDeviceTypeButton>
        ),
    },
    {
      id: 'totalDevices',
      label: 'Total Devices',
      width: '25%',
      align: 'center',
      icon: 'none',
      format: (value: number, row: any) => (
        <TableText
          style={{
            fontWeight: row.isTotal ? 'bold' : 'normal',
            color: value > 0 ? theme?.red : theme?.green,
            textAlign: 'center',
          }}
        >
          {value}
        </TableText>
      ),
    },
    {
      id: 'notRecentlySeen',
      label: 'Devices Not Recently Seen',
      width: '25%',
      align: 'center',
      icon: 'none',
      format: (value: number, row: any) => (
        <TableText
          style={{
            fontWeight: row.isTotal ? 'bold' : 'normal',
            color: value > 0 ? theme?.red : theme?.green,
            textAlign: 'center',
          }}
        >
          {value}
        </TableText>
      ),
    },
    {
      id: 'inventoryIssues',
      label: 'Devices with ServiceNow Inventory Issues',
      width: '25%',
      align: 'center',
      icon: 'none',
      format: (value: number, row: any) => (
        <TableText
          style={{
            fontWeight: row.isTotal ? 'bold' : 'normal',
            color: value > 0 ? theme?.red : theme?.green,
            textAlign: 'center',
          }}
        >
          {value}
        </TableText>
      ),
    },
  ];

  return (
    <Layout
      match={match}
      compactHeader
      headerContent={
        <LayoutHeader
          title={`Retail Hardware Status - ${storeDisplayName}`}
          icon={LayoutHeaderIconTypes.StoreHealth}
        />
      }
    >
      <ScrollToTopOnMount />
      <StyledStoreHC>
        <StyledContainer>
          <div className="storehc-breadcrumb-wrapper">
            <NavCrumb match={match} breadCrumb className="storehc-breadcrumb" />
          </div>
          <Grid container spacing={1} direction="row" alignItems="stretch">
            {/* Key Metrics Section */}
            <Grid item container xs={10}>
              <StyledSectionHeaderRow>
                <StyledSectionHeaderTitle className={'text-color'} variant={'t2'}>
                  {`${storeId} - ${storeDisplayName}`}
                </StyledSectionHeaderTitle>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginLeft: 'auto',
                  }}
                >
                  <SwitchContainer
                    style={{
                      marginRight: '50px',
                    }}
                  >
                    <SwitchLabel>{INVENTORY_ISSUES}</SwitchLabel>
                    <Switch
                      checked={trendingSeriesKey === NETWORK_STATUS}
                      onChange={handleTrendingSwitchToggle}
                      color="default"
                    />
                    <SwitchLabel>{NETWORK_STATUS}</SwitchLabel>
                  </SwitchContainer>
                  <StyledExportButton
                    variant="outlined"
                    color="primary"
                    onClick={handleExportToXlsx}
                    disabled={!storeReportRows.length && !aggregatedStoreReportData.length}
                    startIcon={<Icon path={mdiDownloadOutline} size={0.8} />}
                  >
                    Export to Excel
                  </StyledExportButton>
                </div>
              </StyledSectionHeaderRow>
            </Grid>

            {/* Device Inventory Status Metrics and Chart in First Row */}
            {storeReportLoaded && (
              <>
                {/* Metric Cards Section - Stacked Vertically */}
                <Grid item xs={12} md={3}>
                  <Grid container spacing={1} direction="column">
                    <Grid item xs={12}>
                      <MetricCard
                        label="Total # of Devices"
                        value={totalNumberOfDevices}
                        loading={!storeReportLoaded}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MetricCard
                        label={'# of Devices Failing Tests: '}
                        sublineLabel={totalNumberOfImpactedDevices.toString() || '0'}
                        value={
                          <>
                            <span className="metric-subline">
                              <span className="metric-subline-label">Network:</span>
                              <span
                                className={`metric-subline-value ${
                                  totalNumberOfNotRecentlySeenDevices > 0 ? 'red' : 'green'
                                }`}
                              >
                                {totalNumberOfNotRecentlySeenDevices}
                              </span>
                            </span>
                            <span className="metric-subline">
                              <span className="metric-subline-label"> ServiceNow Inventory:</span>
                              <span
                                className={`metric-subline-value ${
                                  totalNumberOfInventoryIssuesDevices > 0 ? 'red' : 'green'
                                }`}
                              >
                                {totalNumberOfInventoryIssuesDevices}
                              </span>
                            </span>
                          </>
                        }
                        loading={!storeReportLoaded}
                        sublineColorVariant={
                          totalNumberOfInventoryIssuesDevices > 0 ? 'red' : 'green'
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {/* Chart Section */}
                <Grid item xs={12} md={9}>
                  <ApexLineChart
                    data={storeTrendingSeries}
                    loaded={storeTrendingChartLoaded}
                    title={trendingSeriesKey}
                    colors={chartColors}
                    yAxisTitle={yAxisTitle}
                    yAxisMin={
                      trendingSeriesKey === NETWORK_STATUS ? networkStatusYAxisMin : undefined
                    }
                    yAxisMax={trendingSeriesKey === NETWORK_STATUS ? 100 : undefined}
                    selectedTime={selectedChartTime}
                    setSelectedTimeFn={setSelectedChartTime}
                  />
                </Grid>
              </>
            )}

            {/* Latest Store Report and Most Impacted Models in Same Row */}
            <Grid item container xs={12} spacing={2}>
              {/* Latest Store Report Section - Left Side */}
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SGTitle className={'active-color'} variant={'t3'}>
                      Latest Store Report
                    </SGTitle>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper>
                      <Table
                        className="storehc-table-dense"
                        columns={tableColumns.map(c => ({...c, headerWrap: true}))}
                        data={tableData}
                        loaded={storeReportLoaded}
                        paginationText="Devices"
                        noDataText="No store report data available"
                        disablePagination={false}
                        customRowsPerPage={{
                          defaultRowsPerPage: -1,
                          rowsPerPageOptions: [5, 10, 25, {label: 'All', value: -1}],
                        }}
                        customRowClass={(row: any) => (row.isTotal ? 'totals-row' : '')}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              {/* Most Impacted Models Section - Right Side */}
              <Grid item xs={12} md={3}>
                <Grid container spacing={1} direction="column">
                  <Grid item xs={12}>
                    <SGTitle className={'active-color'} variant={'t3'}>
                      Most Impacted Device Categories
                    </SGTitle>
                  </Grid>
                  {topImpactedDevices.length > 0 ? (
                    topImpactedDevices.map(device => (
                      <Grid item xs={12} key={device.deviceType}>
                        <MetricCard
                          label={device.deviceType}
                          value={
                            <>
                              <span className="metric-subline">
                                <span className="metric-subline-label">Network:</span>
                                <span
                                  className={`metric-subline-value ${
                                    device.notRecentlySeen > 0 ? 'red' : 'green'
                                  }`}
                                >
                                  {device.notRecentlySeen}
                                </span>
                              </span>
                              <span className="metric-subline">
                                <span className="metric-subline-label">ServiceNow Inventory:</span>
                                <span
                                  className={`metric-subline-value ${
                                    device.inventoryIssues > 0 ? 'red' : 'green'
                                  }`}
                                >
                                  {device.inventoryIssues}
                                </span>
                              </span>
                            </>
                          }
                          loading={!storeReportLoaded}
                          onClick={() => {
                            logDeviceTypeModalOpenEvent(device.deviceType);
                            setIsAllDeviceTypesModal(false);
                            const devices = deviceDetailsByType[device.deviceType] || [];
                            setSelectedDeviceType(device.deviceType);
                            setSelectedDeviceDevices(devices);
                            setDeviceTableFilters(createDeviceFilters());
                            setHighlightedDeviceSerialForSearch(null);
                            setIsDeviceModalOpen(true);
                          }}
                        />
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <MetricCard
                        label="No impacted devices"
                        value="—"
                        loading={!storeReportLoaded}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </StyledContainer>
      </StyledStoreHC>
      <Modal
        open={isDeviceModalOpen}
        onClose={closeDeviceModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isDeviceModalOpen}>
          <StyledModalBox>
            <StyledModalHeader>
              <div>
                <SGTitle className={'text-color'} variant={'t2'}>
                  {selectedDeviceType || 'Device Details'}
                </SGTitle>
              </div>
              <IconButton onClick={closeDeviceModal}>
                <StyledCloseIcon />
              </IconButton>
            </StyledModalHeader>
            <StyledDeviceList>
              <StyledModalMetricsRow>
                <StyledModalMetricCard>
                  <StyledModalMetricLabel>Total # of Devices</StyledModalMetricLabel>
                  <StyledModalMetricValue>{filteredDeviceRows.length}</StyledModalMetricValue>
                </StyledModalMetricCard>
                <StyledModalMetricCard>
                  <StyledModalMetricLabel># of Devices Failing Tests</StyledModalMetricLabel>
                  <StyledModalMetricValue
                    colorVariant={
                      filteredDeviceRows.filter((device: any) => device.impacted).length > 0
                        ? 'red'
                        : 'green'
                    }
                  >
                    {filteredDeviceRows.filter((device: any) => device.impacted).length}
                  </StyledModalMetricValue>
                </StyledModalMetricCard>
              </StyledModalMetricsRow>
              <StyledModalFilters>
                {visibleDeviceFilterConfig.map(({id, label}) => {
                  const labelId = `device-filter-${id}`;
                  const options = deviceFilterOptions[id];
                  return (
                    <StyledModalFilterControl
                      key={id}
                      variant="outlined"
                      size="small"
                      disabled={!options.length}
                    >
                      <StyledModalFilterLabel id={`${labelId}-label`}>
                        {label}
                      </StyledModalFilterLabel>
                      <StyledModalFilterSelect
                        labelId={labelId}
                        value={deviceTableFilters[id]}
                        onChange={event =>
                          setDeviceTableFilters(prev => ({
                            ...prev,
                            [id]: event.target.value as string,
                          }))
                        }
                        label={label}
                        displayEmpty
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={renderModalFilterValue}
                        MenuProps={modalSelectMenuProps}
                      >
                        <StyledModalFilterMenuItem value="">
                          <em>All</em>
                        </StyledModalFilterMenuItem>
                        {options.map(option => (
                          <StyledModalFilterMenuItem key={option} value={option}>
                            {formatLabel(option)}
                          </StyledModalFilterMenuItem>
                        ))}
                      </StyledModalFilterSelect>
                    </StyledModalFilterControl>
                  );
                })}
                <StyledModalFilterClear
                  type="button"
                  onClick={() => setDeviceTableFilters(createDeviceFilters())}
                  disabled={!hasActiveDeviceFilters}
                >
                  Clear All
                </StyledModalFilterClear>
              </StyledModalFilters>
              <Paper>
                <Table
                  className="storehc-table-dense"
                  columns={(selectedDeviceType === 'MobilePaymentTerminal'
                    ? deviceDetailsTableColumns
                        .filter(c => c.id !== 'lastSeenDate')
                        .map(c => {
                          // Redistribute the removed Last Seen column's width (10%)
                          // to the two inventory columns so their content isn't cramped.
                          if (c.id === 'inventoryCheckStatus') return {...c, width: '20%'};
                          if (c.id === 'itamInventoryStatusDescription')
                            return {...c, width: '20%'};
                          return c;
                        })
                    : deviceDetailsTableColumns
                  ).map(c => ({...c, headerWrap: true}))}
                  data={filteredDeviceRows}
                  loaded={true}
                  paginationText="Devices"
                  noDataText="No store report data available"
                  disablePagination={false}
                  customRowsPerPage={{
                    defaultRowsPerPage: 10,
                    rowsPerPageOptions: [5, 10, 25, {label: 'All', value: -1}],
                  }}
                  customRowClass={deviceModalCustomRowClass}
                />
              </Paper>
              {!filteredDeviceRows.length && (
                <Typography variant="body2" style={{marginTop: '12px'}}>
                  No devices found for this category.
                </Typography>
              )}
            </StyledDeviceList>
          </StyledModalBox>
        </Fade>
      </Modal>
    </Layout>
  );
}

export default StoreHC;
